<template>
  <div id="robot" class="robot-container">
    <div class="header-section">
      <div class="version-info">
        <span class="label">当前版本:</span>
        <span class="value">{{ info.now_version }}</span>
        <i class="el-icon-document-copy copy-icon" @click="handleCopy(info.now_version, '版本号')"></i>
      </div>
      <el-button type="primary" size="mini" icon="el-icon-download" @click="export2CSV">导出CSV</el-button>
    </div>

    <!-- 核心统计概览 -->
    <div class="summary-cards" v-if="v2Stats">
      <div class="card summary-card in">
        <div class="card-title">入款总额</div>
        <div class="card-value">{{ info.in_all_money }}</div>
        <div class="card-footer">净额: {{ v2Stats.totalNetInPesos }}</div>
      </div>
      <div class="card summary-card out">
        <div class="card-title">出款总额</div>
        <div class="card-value">{{ info.out_all_money }}</div>
        <div class="card-footer">合计: {{ info.out_balance }}</div>
      </div>
      <div class="card summary-card unpaid">
        <div class="card-title">未下发U</div>
        <div class="card-value highlighted">{{ v2Stats.unpaidU }}</div>
        <div class="card-footer">应下发: {{ v2Stats.shouldPayoutU }}U</div>
      </div>
    </div>

    <!-- 费率与汇率卡片 -->
    <section class="card-section" v-if="v2Stats">
      <div class="card rate-card">
        <div class="card-header">费率详情</div>
        <div class="rate-grid">
          <div class="rate-item">
            <span class="label">gcash费率:</span>
            <span class="value">{{ v2Stats.setting.oneRate }}</span>
          </div>
          <div class="rate-item" v-if="v2Stats.show.rbaRate">
            <span class="label">银行费率:</span>
            <span class="value">{{ v2Stats.setting.rbaRate }}</span>
          </div>
          <div class="rate-item" v-if="v2Stats.show.rmyRate">
            <span class="label">maya费率:</span>
            <span class="value">{{ v2Stats.setting.rmyRate }}</span>
          </div>
        </div>
      </div>

      <div class="card rate-card" style="margin-top: 15px;">
        <div class="card-header">下发汇率</div>
        <div class="rate-grid">
          <div class="rate-item">
            <span class="label">gcash汇率:</span>
            <span class="value">{{ v2Stats.setting.subUsdRate }}</span>
          </div>
          <div class="rate-item" v-if="v2Stats.show.baUsdRate">
            <span class="label">银行汇率:</span>
            <span class="value">{{ v2Stats.setting.baUsdRate }}</span>
          </div>
          <div class="rate-item" v-if="v2Stats.show.myUsdRate">
            <span class="label">maya汇率:</span>
            <span class="value">{{ v2Stats.setting.myUsdRate }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- 入款明细 -->
    <section class="card-section">
      <div class="card detail-card">
        <div class="card-header">
          <span>入款笔数: {{ info.in_num }}</span>
          <el-tag size="mini" type="success">IN</el-tag>
        </div>
        <div class="group-summary" v-if="info.in_all_detail && info.in_all_detail.length">
          <div v-for="group in info.in_all_detail" :key="group.group_name" class="group-item">
            {{ groupNames[group.group_name] || (group.group_name + '组') }}: {{ group.money }}
          </div>
        </div>
        <table class="detail-table">
          <thead>
            <tr>
              <th>组/时间</th>
              <th>金额</th>
              <th>用户</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(line, index) in info.in_num_detail" :key="'in-' + index">
              <td>
                <div class="group-tag">{{ groupNames[line.group_name] || line.group_name }}</div>
                <div class="time-text">{{ formatTime(line.create_at) }}</div>
              </td>
              <td class="money-cell in-money">{{ line.money }}</td>
              <td class="user-cell">{{ line.user_name }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- 出款明细 -->
    <section class="card-section">
      <div class="card detail-card">
        <div class="card-header">
          <span>出款笔数: {{ info.out_num }}</span>
          <el-tag size="mini" type="danger">OUT</el-tag>
        </div>
        <div class="group-summary" v-if="info.out_all_money_detail && info.out_all_money_detail.length">
          <div v-for="group in info.out_all_money_detail" :key="group.group_name" class="group-item">
            {{ groupNames[group.group_name] || (group.group_name + '组') }}: {{ group.money }}
          </div>
        </div>
        <table class="detail-table">
          <thead>
            <tr>
              <th>组/时间</th>
              <th>金额</th>
              <th>用户</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(line, index) in info.out_num_detail" :key="'out-' + index">
              <td>
                <div class="group-tag">{{ groupNames[line.group_name] || line.group_name }}</div>
                <div class="time-text">{{ formatTime(line.create_at) }}</div>
              </td>
              <td class="money-cell out-money">{{ line.money }}</td>
              <td class="user-cell">{{ line.user_name }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- 下发明细 -->
    <section class="card-section" v-if="v2Stats">
      <div class="card detail-card">
        <div class="card-header">
          <span>下发统计 (v2规则)</span>
          <el-tag size="mini" type="primary">USD</el-tag>
        </div>
        <div class="usd-summary-grid">
          <div class="usd-item">
            <span class="label">应下发:</span>
            <span class="value">{{ v2Stats.shouldPayoutU }} U</span>
          </div>
          <div class="usd-item">
            <span class="label">已下发:</span>
            <span class="value">{{ v2Stats.alreadyPayoutU }} U</span>
          </div>
          <div class="usd-item highlight">
            <span class="label">未下发:</span>
            <span class="value">{{ v2Stats.unpaidU }} U</span>
          </div>
        </div>
        <table class="detail-table" v-if="info.usd_num_detail && info.usd_num_detail.length">
          <thead>
            <tr>
              <th>时间</th>
              <th>金额(P)</th>
              <th>用户</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(line, index) in info.usd_num_detail" :key="'usd-' + index">
              <td class="time-text">{{ formatTime(line.create_at) }}</td>
              <td class="money-cell">{{ line.money }}</td>
              <td class="user-cell">{{ line.user_name }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- 分用户统计 -->
    <section class="card-section">
      <div class="card summary-card-full">
        <div class="card-header">分用户统计</div>
        <table class="user-summary-table">
          <thead>
            <tr>
              <th>用户</th>
              <th>入款(U)</th>
              <th>出款(U)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(user, index) in (info.shows || [])" :key="'user-' + index">
              <td class="user-name">{{ user.user_name }}</td>
              <td class="in-money">{{ user.in_balance_u }}u</td>
              <td class="out-money">{{ user.out_balance_u }}u</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>

<style lang="less" scoped>
.robot-container {
  padding: 15px;
  background-color: #f5f7fa;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  color: #333;

  .header-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding: 0 5px;

    .version-info {
      .label {
        color: #909399;
        font-size: 14px;
      }
      .value {
        margin-left: 8px;
        font-weight: 600;
        color: #303133;
      }
      .copy-icon {
        margin-left: 5px;
        color: #409eff;
        cursor: pointer;
        font-size: 14px;
        &:active { opacity: 0.7; }
      }
    }
  }

  .summary-cards {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    margin-bottom: 20px;
  }

  .card {
    background: #fff;
    border-radius: 8px;
    padding: 12px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
    border: 1px solid #ebeef5;

    .card-title {
      font-size: 12px;
      color: #909399;
      margin-bottom: 8px;
    }

    .card-value {
      font-size: 16px;
      font-weight: bold;
      margin-bottom: 6px;
      word-break: break-all;
    }

    .card-footer {
      font-size: 11px;
      color: #909399;
    }

    &.in {
      border-left: 4px solid #67c23a;
      .card-value { color: #67c23a; }
    }
    &.out {
      border-left: 4px solid #f56c6c;
      .card-value { color: #f56c6c; }
    }
    &.unpaid {
      border-left: 4px solid #409eff;
      .card-value.highlighted { color: #409eff; font-size: 18px; }
    }
  }

  .card-section {
    margin-bottom: 20px;

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-bottom: 10px;
      margin-bottom: 10px;
      border-bottom: 1px solid #f0f2f5;
      font-weight: 600;
      font-size: 15px;
    }
  }

  .rate-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;

    .rate-item {
      display: flex;
      flex-direction: column;
      .label { font-size: 12px; color: #909399; }
      .value { font-size: 14px; font-weight: 500; color: #303133; margin-top: 2px; }
    }
  }

  .group-summary {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 12px;
    background: #f8f9fb;
    padding: 8px;
    border-radius: 4px;

    .group-item {
      font-size: 12px;
      color: #606266;
      background: #fff;
      padding: 2px 8px;
      border: 1px solid #e4e7ed;
      border-radius: 10px;
    }
  }

  .detail-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th {
      text-align: left;
      color: #909399;
      font-weight: 500;
      padding: 8px 4px;
      border-bottom: 1px solid #f0f2f5;
    }

    td {
      padding: 10px 4px;
      border-bottom: 1px solid #f9fafc;
      vertical-align: top;
    }

    .group-tag {
      font-size: 11px;
      color: #409eff;
      font-weight: 600;
    }

    .time-text {
      font-size: 11px;
      color: #c0c4cc;
      margin-top: 2px;
    }

    .money-cell {
      font-weight: 600;
      &.in-money { color: #67c23a; }
      &.out-money { color: #f56c6c; }
    }

    .user-cell {
      color: #606266;
      max-width: 80px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .usd-summary-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    margin-bottom: 15px;
    background: #ecf5ff;
    padding: 10px;
    border-radius: 6px;

    .usd-item {
      display: flex;
      flex-direction: column;
      .label { font-size: 11px; color: #409eff; }
      .value { font-size: 13px; font-weight: 600; color: #303133; }
      &.highlight .value { color: #409eff; }
    }
  }

  .user-summary-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th {
      padding: 10px;
      background: #f5f7fa;
      color: #909399;
      text-align: center;
    }

    td {
      padding: 12px 10px;
      text-align: center;
      border-bottom: 1px solid #f0f2f5;

      &.user-name { font-weight: 500; text-align: left; }
      &.in-money { color: #67c23a; font-weight: 600; }
      &.out-money { color: #f56c6c; font-weight: 600; }
    }
  }
}
</style>

<script>
import { getInfo } from "@/api/robot";
import saveTextAsFile from "@/utils/saveTextAsFile";
import copy from "copy-to-clipboard";

export default {
  name: "Robot",
  data() {
    return {
      info: {
        now_version: "",
        in_num: 0,
        in_num_detail: [],
        out_num: 0,
        out_num_detail: [],
        usd_num: 0,
        usd_num_detail: [],
        in_all_money: "0",
        in_balance: "0",
        out_all_money: "0",
        out_balance: "0",
        usd_all_money: "0",
        usd_all_money_u: "0",
        should_balance_u: "0",
        balance_u: "0",
        in_rate: "0",
        out_rate: "0",
        usd_rate: "0",
        metas: [],
        shows: [],
        open_group: false
      },
      groupNames: {
        1: "gcash组",
        2: "银行卡组",
        3: "maya组",
        0: "未知组"
      },
    };
  },
  computed: {
    // 按 v2 规则计算核心数据
    v2Stats() {
      if (!this.info || !this.info.metas || !this.info.metas.length) return null;

      // 1. 获取最新配置 (Type 4 or 8)
      const setting = [...this.info.metas].reverse().find(m => m.type === 4 || m.type === 8) || {};

      const oneRate = Number(setting.one_rate || 0);
      const rbaRateRaw = Number(setting.rba_rate || 0);
      const rmyRateRaw = Number(setting.rmy_rate || 0);
      const subUsdRate = Number(setting.sub_usd_rate || 0) || Number(setting.usd_rate || 0);
      const baUsdRateRaw = Number(setting.ba_usd_rate || 0);
      const myUsdRateRaw = Number(setting.my_usd_rate || 0);
      const addUsdRate = Number(setting.add_usd_rate || 0) || subUsdRate;

      // 实际计算时使用的费率 (如果有专有费率则用专有的，否则用默认 oneRate)
      const calcRbaRate = rbaRateRaw || oneRate;
      const calcRmyRate = rmyRateRaw || oneRate;
      const calcBaUsdRate = baUsdRateRaw || subUsdRate;
      const calcMyUsdRate = myUsdRateRaw || subUsdRate;

      // 2. 统计各组入款净额 (Net In Pesos)
      const inGroupSummary = {};
      (this.info.in_num_detail || []).forEach(inItem => {
        const group = inItem.group_name || 0;
        if (!inGroupSummary[group]) inGroupSummary[group] = { gross: 0, net: 0 };

        let rate = oneRate;
        if (group === 2) rate = calcRbaRate;
        else if (group === 3) rate = calcRmyRate;

        const money = Number(inItem.money || 0);
        const netAmount = money * (1 - rate);

        if (inItem.type_name === 1) {
          inGroupSummary[group].gross += money;
          inGroupSummary[group].net += netAmount;
        } else if (inItem.type_name === 32) {
          inGroupSummary[group].gross -= money;
          inGroupSummary[group].net -= netAmount;
        }
      });

      // 3. 统计各组出款
      const groupOutPesos = {};
      (this.info.out_num_detail || []).forEach(outItem => {
        const group = outItem.group_name || 0;
        if (!groupOutPesos[group]) groupOutPesos[group] = 0;

        const money = Number(outItem.money || 0);
        if (outItem.type_name === 2) {
          groupOutPesos[group] += money;
        } else if (outItem.type_name === 64) {
          groupOutPesos[group] -= money;
        }
      });

      // 4. 计算应下发U (Should Payout U)
      let totalNetInPesos = 0;
      let totalGrossU = 0;
      const allGroups = new Set([...Object.keys(inGroupSummary), ...Object.keys(groupOutPesos)]);
      const groupGrossUMap = {};

      allGroups.forEach(group => {
        const g = Number(group);
        let usdRate = subUsdRate;
        if (g === 2) usdRate = calcBaUsdRate;
        else if (g === 3) usdRate = calcMyUsdRate;

        const netInPesos = (inGroupSummary[group] && inGroupSummary[group].net) || 0;
        totalNetInPesos += netInPesos;

        const pesosNetAfterOut = netInPesos - (groupOutPesos[group] || 0);
        const grossU = usdRate > 0 ? pesosNetAfterOut / usdRate : 0;

        groupGrossUMap[group] = grossU;
        totalGrossU += grossU;
      });

      const shouldPayoutU = totalGrossU;
      const alreadyPayoutU = Number(this.info.usd_all_money_u_meta || this.info.usd_all_money_u || 0);
      const unpaidU = shouldPayoutU - alreadyPayoutU;

      return {
        shouldPayoutU: shouldPayoutU.toFixed(2),
        alreadyPayoutU: alreadyPayoutU.toFixed(2),
        unpaidU: unpaidU.toFixed(2),
        totalNetInPesos: totalNetInPesos.toFixed(2),
        groupGrossUMap: groupGrossUMap,
        // 控制显示逻辑
        show: {
          rbaRate: rbaRateRaw > 0,
          rmyRate: rmyRateRaw > 0,
          baUsdRate: baUsdRateRaw > 0,
          myUsdRate: myUsdRateRaw > 0
        },
        setting: {
          oneRate: (oneRate * 100).toFixed(2) + "%",
          rbaRate: (rbaRateRaw * 100).toFixed(2) + "%",
          rmyRate: (rmyRateRaw * 100).toFixed(2) + "%",
          subUsdRate: subUsdRate.toFixed(2),
          baUsdRate: baUsdRateRaw.toFixed(2),
          myUsdRate: myUsdRateRaw.toFixed(2),
          addUsdRate: addUsdRate.toFixed(2)
        }
      };
    }
  },
  async mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      const params = this.$route.query;
      try {
        const res = await getInfo(params);
        if (res.code === 0) {
          this.info = res.data;
        } else {
          this.$message.error(res.msg || "获取数据失败");
        }
      } catch (error) {
        this.$message.error("网络错误，请稍后重试");
      }
    },
    formatTime(timestamp) {
      if (!timestamp) return "";
      const ts = String(timestamp).length === 10 ? Number(timestamp) * 1000 : Number(timestamp);
      const date = new Date(ts);
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      const h = String(date.getHours()).padStart(2, "0");
      const i = String(date.getMinutes()).padStart(2, "0");
      const s = String(date.getSeconds()).padStart(2, "0");
      return `${m}-${d} ${h}:${i}:${s}`;
    },
    handleCopy(text, label) {
      if (copy(text)) {
        this.$message.success(`${label}已复制`);
      } else {
        this.$message.error("复制失败");
      }
    },
    convertData(data) {
      const stats = this.v2Stats;
      let arr = [];

      if (data.now_version) {
        arr.push(`当前版本:, ${data.now_version}`);
        arr.push(``);
      }

      arr.push(`概览统计 (v2规则)`);
      arr.push(`入款总额:, ${data.in_all_money}`);
      arr.push(`入款合计(净):, ${(stats && stats.totalNetInPesos) || data.in_balance}`);
      arr.push(`出款总额:, ${data.out_all_money}`);
      arr.push(`应下发U:, ${(stats && stats.shouldPayoutU) || data.should_balance_u}`);
      arr.push(`已下发U:, ${(stats && stats.alreadyPayoutU) || data.usd_all_money_u}`);
      arr.push(`未下发U:, ${(stats && stats.unpaidU) || data.balance_u}`);
      arr.push(``);

      if (stats && stats.setting) {
      arr.push(`费率与汇率`);
      arr.push(`gcash费率:, ${stats.setting.oneRate}`);
      arr.push(`银行费率:, ${stats.setting.rbaRate}`);
      arr.push(`maya费率:, ${stats.setting.rmyRate}`);
      arr.push(`gcash汇率:, ${stats.setting.subUsdRate}`);
      arr.push(`银行汇率:, ${stats.setting.baUsdRate}`);
      arr.push(`maya汇率:, ${stats.setting.myUsdRate}`);

        arr.push(``);
      }

      arr.push(`入款明细 (笔数: ${data.in_num || 0})`);
      if (data.in_num_detail && data.in_num_detail.length) {
        arr.push(`组数,时间,金额,用户`);
        data.in_num_detail.forEach((line) => {
          arr.push(`${line.group_name},${this.formatTime(line.create_at)},${line.money},${line.user_name}`);
        });
      }
      arr.push(``);

      arr.push(`出款明细 (笔数: ${data.out_num || 0})`);
      if (data.out_num_detail && data.out_num_detail.length) {
        arr.push(`组数,时间,金额,用户`);
        data.out_num_detail.forEach((line) => {
          arr.push(`${line.group_name},${this.formatTime(line.create_at)},${line.money},${line.user_name}`);
        });
      }
      arr.push(``);

      return arr;
    },
    export2CSV() {
      const fileName = `${this.info.now_version.replace(/ /g, "").replace(/:/g, "_")}_${this.$route.query.chat_id}.csv`;
      saveTextAsFile(this.convertData(this.info).join("\r\n"), fileName);
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/views/RobotRefno.vue